package com.islamicmedia.shared.domain

import com.islamicmedia.shared.domain.model.MediaType
import io.ktor.http.encodeURLQueryComponent

/**
 * "Where to get it" link for an analysed title, shown under the verdict.
 *
 *  - Books / Games  → the user's local Amazon store. This is an AFFILIATE link that earns — but only
 *    on GREEN titles (score >= 7). We never take a cut on anything we flagged (yellow/red get the same
 *    link with no tag), which keeps the ratings provably independent of the money.
 *  - Movies / TV    → JustWatch "where to watch" (stream/rent/buy across every service). No commission.
 *  - YouTube        → a channel search. No commission.
 *
 * The Amazon associate tag is PER-MARKETPLACE: each country's Amazon is a separate Associates program,
 * so a tag only earns in its own store. Fill a market's tag once you've enrolled in that program;
 * markets left blank still get a working (unpaid) link. See [MARKETS].
 */
/** Which icon the button shows: a bag for "buy", a play for "watch/find". */
enum class AccessLinkKind { SHOP, WATCH }

data class AccessLink(
    val label: String,
    val url: String,
    /** True when the URL actually carries our tag (green title + an enrolled market) → show the disclosure. */
    val isAmazonAffiliate: Boolean,
    val kind: AccessLinkKind,
)

private data class Market(val amazonDomain: String, val amazonTag: String, val justWatchLocale: String)

// ⬇️ AFFILIATE TAGS GO HERE. Sign up at affiliate-program.amazon.com (one program per country) and
// paste each tag below. A blank tag = the link still works, it just earns nothing until you enroll.
// amazon.ae (UAE) is its own program and is NOT covered by Amazon OneLink — enroll separately to earn.
private val MARKETS = mapOf(
    "US" to Market("amazon.com",   "REPLACE-US-20", "us"),
    "GB" to Market("amazon.co.uk", "REPLACE-UK-21", "uk"),
    "CA" to Market("amazon.ca",    "",              "ca"),
    "AU" to Market("amazon.com.au", "",             "au"),
    "AE" to Market("amazon.ae",    "",              "ae"),
    "SA" to Market("amazon.sa",    "",              "sa"),
    "DE" to Market("amazon.de",    "",              "de"),
)
private val DEFAULT_MARKET = MARKETS.getValue("US")

/** Device region as an ISO-3166 alpha-2 code (e.g. "US", "AE"). Falls back to "US". */
expect fun deviceCountryCode(): String

// Master switch: ALL "where to get it" buttons (books, games, YouTube, movies/TV) are OFF while the
// overall approach is being decided. Flip to true to re-enable everything (movies/TV also needs its
// own line restored in the `when` below).
private val ACCESS_LINKS_ENABLED = false

/**
 * Build the access link for a title, or null if we can't (blank title). [isGreen] gates the earning
 * tag; [country] defaults to the device region but is injectable for tests.
 */
fun accessLinkFor(
    title: String,
    mediaType: MediaType,
    isGreen: Boolean,
    // Book author (null for non-books). Added to the Amazon query so the search lands on the right
    // edition instead of a same-titled book by someone else. No exact ASIN link — the ISBN is dropped
    // before result time, so this title+author search is the closest we get without re-plumbing it.
    author: String? = null,
    country: String = deviceCountryCode(),
): AccessLink? {
    if (!ACCESS_LINKS_ENABLED) return null
    // Strip a trailing "(2019)" year so the search query is clean.
    val cleanTitle = title.replace(Regex("\\s*\\(\\d{4}\\)\\s*$"), "").trim()
    if (cleanTitle.isEmpty()) return null
    val queryText = if (!author.isNullOrBlank()) "$cleanTitle ${author.trim()}" else cleanTitle
    val market = MARKETS[country.uppercase()] ?: DEFAULT_MARKET
    val q = queryText.encodeURLQueryComponent(spaceToPlus = true)

    fun amazon(dept: String, label: String): AccessLink {
        val earns = isGreen && market.amazonTag.isNotEmpty()
        val tag = if (earns) "&tag=${market.amazonTag}" else ""
        return AccessLink(label, "https://www.${market.amazonDomain}/s?k=$q$dept$tag", isAmazonAffiliate = earns, kind = AccessLinkKind.SHOP)
    }

    return when (mediaType) {
        MediaType.BOOKS -> amazon("&i=stripbooks", "Get the book")
        MediaType.GAMES -> amazon("&i=videogames", "Get the game")
        // "Where to watch" (JustWatch) is HIDDEN for now while the per-service streaming approach is
        // being decided. To restore, return this instead of null:
        //   AccessLink("Where to watch", "https://www.justwatch.com/${market.justWatchLocale}/search?q=$q",
        //              isAmazonAffiliate = false, kind = AccessLinkKind.WATCH)
        MediaType.MOVIES, MediaType.TV_SHOWS -> null
        MediaType.YOUTUBE ->
            AccessLink("Find on YouTube", "https://www.youtube.com/results?search_query=$q", isAmazonAffiliate = false, kind = AccessLinkKind.WATCH)
    }
}
